/**
 * Personal Bear Analyzer (spec sections 26, 27, 60).
 *
 * Everything on this screen is derived from recorded observations of real
 * sessions, so every figure travels with its sample count. That is not
 * decoration: a 27% join advantage over three rallies and over forty rallies
 * are different claims, and the spec forbids presenting them alike
 * (section 28).
 *
 * The recommendation at the top is a recommendation, not a verdict. It carries
 * its confidence, its reasons, and its warnings, and it degrades to
 * INSUFFICIENT_DATA rather than guessing when the history is too thin. The
 * Korean headline keeps that hedge ("...쪽이 더 나아 보임"); a flat assertion
 * would turn a recommendation into a verdict in translation alone.
 */

import type { PersonalBearAnalysisDto } from '@kso/schemas';
import type { BearAnalysisResult } from '../../api/queries';
import { Card, Notice, ReasonList, Stat, WarningList } from '../../components/ui/primitives';
import { EmptyState } from '../../components/ui/states';
import {
  ConfidenceBadge,
  InferenceMark,
  LOW_SAMPLE_THRESHOLD,
  SampleCount,
} from '../../components/inference/Inference';
import { formatCompact, formatDateTime, formatDuration, formatNumber, formatPercent } from '../../lib/format';
import { useT } from '../../i18n';
import { UpgradeImpactPanel } from './UpgradeImpactPanel';

type BreakdownRow = PersonalBearAnalysisDto['byFormation'][number];
type Side = PersonalBearAnalysisDto['lead'];

/**
 * Renders either the analysis or the server's explicit "nothing recorded" state.
 *
 * Emptiness is not inferred from zeroed fields here. The API returns an
 * `empty` flag precisely so that "no rallies have been entered" cannot be
 * confused with "analysed, and the answer was zero" — those are different
 * claims and only one of them is a measurement.
 */
export function BearAnalysisView({ result }: { result: BearAnalysisResult }) {
  const t = useT();

  if (result.empty) {
    return (
      <EmptyState
        title={t.bearAnalysis.emptyTitle}
        body={
          <>
            <p style={{ marginTop: 0 }}>{result.message}</p>
            <p style={{ marginBottom: 0 }}>{t.bearAnalysis.emptyBody}</p>
          </>
        }
      />
    );
  }

  return <PersonalBearAnalyzer analysis={result.analysis} />;
}

export function PersonalBearAnalyzer({ analysis }: { analysis: PersonalBearAnalysisDto }) {
  const t = useT();
  const totalSamples = analysis.lead.samples + analysis.join.samples;

  return (
    <div className="stack">
      <Card
        title={
          <span className="row">
            {t.bearAnalysis.recommendationTitle}{' '}
            <InferenceMark modelVersion={analysis.modelVersion} />
          </span>
        }
        actions={
          <ConfidenceBadge level={analysis.confidenceLevel} confidence={analysis.confidence} />
        }
      >
        <div className="row" style={{ alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontSize: 22, fontWeight: 600 }}>
            {t.bearAnalysis.roleHeadline[analysis.recommendedRole]}
          </span>
          <SampleCount samples={totalSamples} noun={t.inference.nouns.recordedRallies} />
        </div>

        <div className="grid grid--stats" style={{ marginTop: 12 }}>
          <Stat
            label={t.bearAnalysis.leadAverage}
            value={formatCompact(analysis.lead.average)}
            foot={<SampleCount samples={analysis.lead.samples} noun={t.inference.nouns.leads} />}
            absentLabel={t.bearAnalysis.neverLed}
          />
          <Stat
            label={t.bearAnalysis.joinAverage}
            value={formatCompact(analysis.join.average)}
            foot={<SampleCount samples={analysis.join.samples} noun={t.inference.nouns.joins} />}
            absentLabel={t.bearAnalysis.neverJoined}
          />
          <Stat
            label={t.bearAnalysis.leadVsJoin}
            value={formatPercent(analysis.leadVsJoinAdvantage)}
            foot={<span className="faint">{t.bearAnalysis.leadVsJoinFoot}</span>}
            absentLabel={t.bearAnalysis.notComparable}
          />
          <Stat
            label={t.bearAnalysis.returnCycle}
            value={formatDuration(analysis.returnCycleTimeSeconds)}
            foot={<span className="faint">{t.bearAnalysis.returnCycleFoot}</span>}
            absentLabel={t.common.notRecordedTitle}
          />
        </div>

        {analysis.recommendedRole === 'INSUFFICIENT_DATA' ? (
          <div style={{ marginTop: 10 }}>
            <Notice severity="WARN">{t.bearAnalysis.insufficientNotice}</Notice>
          </div>
        ) : null}

        <div style={{ marginTop: 10 }}>
          <h3 className="muted small">{t.common.why}</h3>
          <ReasonList reasons={analysis.reasons} />
        </div>

        <div style={{ marginTop: 10 }}>
          <WarningList warnings={analysis.warnings} />
        </div>

        <p className="faint small" style={{ marginBottom: 0, marginTop: 10 }}>
          {t.bearAnalysis.computedFrom(
            formatDateTime(analysis.sourceDataAt) ?? t.bearAnalysis.unrecordedTime,
          )}
        </p>
      </Card>

      <div className="grid grid--halves">
        <SideCard title={t.bearAnalysis.leadPerformance} side={analysis.lead} />
        <SideCard title={t.bearAnalysis.joinPerformance} side={analysis.join} />
      </div>

      <Card title={t.bearAnalysis.bestJoinTargets}>
        <p className="muted small" style={{ marginTop: 0 }}>
          {t.bearAnalysis.bestJoinTargetsBlurb}
        </p>
        <BreakdownTable
          rows={analysis.bestJoinTargets}
          labelHeader={t.bearAnalysis.rallyLead}
          emptyMessage={t.bearAnalysis.noJoinTargets}
        />
      </Card>

      <div className="grid grid--halves">
        <Card title={t.bearAnalysis.byCaptainHero}>
          <BreakdownTable
            rows={analysis.byCaptainHero}
            labelHeader={t.bearAnalysis.captainHero}
            emptyMessage={t.bearAnalysis.noCaptainHero}
          />
        </Card>

        <Card title={t.bearAnalysis.formationComparison}>
          <p className="muted small" style={{ marginTop: 0 }}>
            {t.bearAnalysis.formationBlurb}
          </p>
          <BreakdownTable
            rows={analysis.byFormation}
            labelHeader={t.bearAnalysis.formation}
            emptyMessage={t.bearAnalysis.noFormation}
            showNormalized
          />
        </Card>
      </div>

      <Card title={t.bearAnalysis.byWave}>
        <BreakdownTable
          rows={analysis.byWave}
          labelHeader={t.common.wave}
          emptyMessage={t.bearAnalysis.noWaves}
        />
      </Card>

      <TrendsCard trends={analysis.trends} />

      <UpgradeImpactPanel impacts={analysis.upgradeImpacts} />
    </div>
  );
}

function SideCard({ title, side }: { title: string; side: Side }) {
  const t = useT();

  if (side.samples === 0) {
    return (
      <Card title={title}>
        <p className="absent small" style={{ margin: 0 }}>
          {t.bearAnalysis.noRalliesInRole}
        </p>
      </Card>
    );
  }

  return (
    <Card
      title={title}
      actions={<SampleCount samples={side.samples} noun={t.inference.nouns.rallies} />}
    >
      <dl className="kv">
        <dt>{t.bearAnalysis.averageDamage}</dt>
        <dd>
          {formatCompact(side.average) ?? <span className="absent">{t.common.notCalculable}</span>}
        </dd>
        <dt>{t.bearAnalysis.medianDamage}</dt>
        <dd>
          {formatCompact(side.median) ?? <span className="absent">{t.common.notCalculable}</span>}
        </dd>
        <dt>{t.bearAnalysis.bestRecorded}</dt>
        <dd>
          {formatCompact(side.best) ?? <span className="absent">{t.common.notCalculable}</span>}
        </dd>
        <dt title={t.bearAnalysis.damagePerTroopTitle}>{t.bearAnalysis.damagePerTroop}</dt>
        <dd>
          {side.damagePerTroop === null ? (
            <span className="absent">{t.bearAnalysis.noTroopCounts}</span>
          ) : (
            formatNumber(Math.round(side.damagePerTroop))
          )}
        </dd>
      </dl>
      {side.samples < LOW_SAMPLE_THRESHOLD ? (
        <div style={{ marginTop: 8 }}>
          <Notice severity="WARN">{t.bearAnalysis.thinRoleSamples(side.samples)}</Notice>
        </div>
      ) : null}
    </Card>
  );
}

function BreakdownTable({
  rows,
  labelHeader,
  emptyMessage,
  showNormalized = false,
}: {
  rows: readonly BreakdownRow[];
  labelHeader: string;
  emptyMessage: string;
  showNormalized?: boolean;
}) {
  const t = useT();

  if (rows.length === 0) {
    return (
      <p className="absent small" style={{ margin: 0 }}>
        {emptyMessage}
      </p>
    );
  }

  const thin = rows.filter((row) => row.samples < LOW_SAMPLE_THRESHOLD).length;
  const columns = t.bearAnalysis.breakdownColumns;

  return (
    <>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>{columns.rank}</th>
              <th>{labelHeader}</th>
              <th className="num">{columns.mean}</th>
              <th className="num">{columns.median}</th>
              <th className="num">{columns.best}</th>
              {showNormalized ? <th className="num">{columns.normalized}</th> : null}
              <th>{t.common.samples}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.key}>
                <td className="num faint">{index + 1}</td>
                {/* Row labels are player, hero and formation names from the
                    server — rendered verbatim, never translated. */}
                <td>{row.label}</td>
                <td className="num">{formatCompact(row.mean)}</td>
                <td className="num">{formatCompact(row.median)}</td>
                <td className="num">{formatCompact(row.best)}</td>
                {showNormalized ? (
                  <td className="num">
                    {t.bearAnalysis.normalizedValue(row.normalized.toFixed(2))}
                  </td>
                ) : null}
                <td>
                  <SampleCount samples={row.samples} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {thin > 0 ? (
        <p className="small" style={{ marginBottom: 0, color: 'var(--low)' }}>
          {t.bearAnalysis.thinRows(thin, rows.length, LOW_SAMPLE_THRESHOLD)}
        </p>
      ) : null}
    </>
  );
}

function TrendsCard({ trends }: { trends: PersonalBearAnalysisDto['trends'] }) {
  const t = useT();
  const columns = t.bearAnalysis.trendColumns;

  const windows = [
    { label: t.bearAnalysis.trendWindows.last3, trend: trends.last3 },
    { label: t.bearAnalysis.trendWindows.last5, trend: trends.last5 },
    { label: t.bearAnalysis.trendWindows.last10, trend: trends.last10 },
  ];

  return (
    <Card title={t.bearAnalysis.trendsTitle}>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>{columns.window}</th>
              <th className="num">{columns.meanDamage}</th>
              <th className="num">{columns.medianDamage}</th>
              <th className="num">{columns.changeVsPrevious}</th>
              <th>{columns.sessions}</th>
            </tr>
          </thead>
          <tbody>
            {windows.map(({ label, trend }) => (
              <tr key={label}>
                <td>{label}</td>
                <td className="num">
                  {formatCompact(trend.meanDamage) ?? (
                    <span className="absent">{t.common.dash}</span>
                  )}
                </td>
                <td className="num">
                  {formatCompact(trend.medianDamage) ?? (
                    <span className="absent">{t.common.dash}</span>
                  )}
                </td>
                <td className="num">
                  {trend.changeVsPrevious === null ? (
                    <span className="absent">{t.bearAnalysis.noEarlierWindow}</span>
                  ) : (
                    formatPercent(trend.changeVsPrevious)
                  )}
                </td>
                <td>
                  <SampleCount
                    samples={trend.sessions}
                    noun={t.inference.nouns.sessions}
                    threshold={3}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/*
        Describes what happened between two windows. It is deliberately not
        called improvement: everything varied between them.
      */}
      <p className="faint small" style={{ marginBottom: 0 }}>
        {t.bearAnalysis.trendsCaveat}
      </p>
    </Card>
  );
}
